'use client'
import React, { ReactElement, useState } from 'react'
import type { HotelRating } from '@/types/Misc'
import AngleDownIcon from '@/icons/font-awesome/sharp-light/angle-down.svg'
import AngleUpIcon from '@/icons/font-awesome/sharp-light/angle-up.svg'
import { useTranslations } from 'next-intl'
import StarIcon from '@/icons/font-awesome/sharp-solid/star.svg'
import { HotelRatingRatingItem } from './HotelRatingRatingItem'

function getValues(value: number): number[] {
  const wholeNumber = Math.floor(value)
  const decimalPartOfNumber = value - wholeNumber
  const result: number[] = []

  for (let i = 0; i < wholeNumber; i++) {
    result.push(100)
  }
  const roundedDecimalNumber = Math.round(decimalPartOfNumber * 10) / 10

  if (roundedDecimalNumber !== 0) {
    result.push(roundedDecimalNumber * 100)
  }
  return result
}

export function HotelRating(props: { hotelRating: HotelRating }): ReactElement | null {
  const values = getValues(props.hotelRating.ratingOverall)

  const stars1 = values[0] || 0
  const stars2 = values[1] || 0
  const stars3 = values[2] || 0
  const stars4 = values[3] || 0
  const stars5 = values[4] || 0

  const [open, setOpen] = useState(false)
  const t = useTranslations('Hotel.HotelRatings.HotelRating')
  const travelerInformation = [
    props.hotelRating.travellerType,
    props.hotelRating.travellerAge,
    props.hotelRating.travelDuration,
  ].filter((n) => n !== null && n !== undefined && n !== '' && n !== ' ')

  const showHotelRatingDetails =
    (props.hotelRating.textFood !== null && props.hotelRating.textFood !== '') ||
    (props.hotelRating.textSpa !== null && props.hotelRating.textSpa !== '') ||
    (props.hotelRating.textHotel !== null && props.hotelRating.textHotel !== '') ||
    (props.hotelRating.textService !== null && props.hotelRating.textService !== '') ||
    (props.hotelRating.textTreatments !== null && props.hotelRating.textTreatments !== '')

  return (
    <div className="mb-4 flex flex-col justify-center gap-2 rounded-md bg-white px-10 pb-5 pt-8 shadow-md">
      {props.hotelRating.summary && (
        <div className="text-center text-xl font-bold text-stone-900">
          {props.hotelRating.summary}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-center gap-2">
        {props.hotelRating.ratingOverall > 0 && (
          <div className="flex h-8 items-center justify-center rounded-md text-white">
            <div className={`flex`}>
              <StarIcon
                className={`mr-1 h-5 w-5 rounded-md fill-primary-complement p-1 md:h-6 md:w-6`}
                style={{
                  background: `linear-gradient(90deg,rgb(var(--color-primary))${stars1}%,rgb(221,221,221)${stars1}%)`,
                }}
              />
              <StarIcon
                className={`mr-1 h-5 w-5 rounded-md fill-primary-complement p-1 md:h-6 md:w-6`}
                style={{
                  background: `linear-gradient(90deg,rgb(var(--color-primary))${stars2}%,rgb(221,221,221)${stars2}%)`,
                }}
              />
              <StarIcon
                className={`mr-1 h-5 w-5 rounded-md fill-primary-complement p-1 md:h-6 md:w-6`}
                style={{
                  background: `linear-gradient(90deg,rgb(var(--color-primary))${stars3}%,rgb(221,221,221)${stars3}%)`,
                }}
              />
              <StarIcon
                className={`mr-1 h-5 w-5 rounded-md fill-primary-complement p-1 md:h-6 md:w-6`}
                style={{
                  background: `linear-gradient(90deg,rgb(var(--color-primary))${stars4}%,rgb(221,221,221)${stars4}%)`,
                }}
              />
              <StarIcon
                className={`h-5 w-5 rounded-md fill-primary-complement p-1 md:h-6 md:w-6`}
                style={{
                  background: `linear-gradient(90deg,rgb(var(--color-primary))${stars5}%,rgb(221,221,221)${stars5}%)`,
                }}
              />
            </div>
          </div>
        )}
        {props.hotelRating.travellerName && (
          <p className="font-bold text-stone-900">{props.hotelRating.travellerName}</p>
        )}
        <p className="text-stone-400">{props.hotelRating.travelDate}</p>
      </div>
      {travelerInformation && (
        <p className=" mb-4 flex flex-nowrap justify-center text-md text-stone-400 md:text-xs">
          {travelerInformation.join(' | ')}
        </p>
      )}
      {props.hotelRating.textOverall && (
        <div>
          <p
            className={`pt-2 text-center text-sm text-stone-800 ${
              !open && showHotelRatingDetails ? 'line-clamp-3' : ''
            }`}
          >
            {props.hotelRating.textOverall}
          </p>
        </div>
      )}
      {showHotelRatingDetails && (
        <div className={`min-h-0 overflow-hidden ${open ? '' : 'hidden'}`}>
          <HotelRatingRatingItem
            rating={props.hotelRating.ratingHotel}
            text={props.hotelRating.textHotel}
            headline={t('ratingHotelText')}
          />
          <HotelRatingRatingItem
            rating={props.hotelRating.ratingService}
            text={props.hotelRating.textService}
            headline={t('ratingServiceText')}
          />
          <HotelRatingRatingItem
            rating={props.hotelRating.ratingTreatments}
            text={props.hotelRating.textTreatments}
            headline={t('ratingTreatmentsText')}
          />
          <HotelRatingRatingItem
            rating={props.hotelRating.ratingFood}
            text={props.hotelRating.textFood}
            headline={t('ratingFoodText')}
          />
          <HotelRatingRatingItem
            rating={props.hotelRating.ratingSpa}
            text={props.hotelRating.textSpa}
            headline={t('ratingSpaText')}
          />
        </div>
      )}

      {showHotelRatingDetails && (
        <div className="flex justify-center">
          <button
            className="xs:text-xs justify-center p-3 text-sm text-stone-800"
            onClick={() => setOpen(!open)}
          >
            {open ? (
              <p
                className="flex justify-center text-stone-400 
        "
              >
                {t('buttonCloseText')} <AngleUpIcon className="ml-2 mt-1 h-4 w-4 fill-stone-400" />
              </p>
            ) : (
              <p
                className="flex justify-center text-stone-400 
        "
              >
                {t('buttonOpenText')} <AngleDownIcon className="ml-2 mt-1 h-4 w-4 fill-stone-400" />
              </p>
            )}
          </button>
        </div>
      )}
      {props.hotelRating.comment && (
        <div className="mt-2 border-t-[1px]">
          <p className="my-2 font-bold text-stone-900">{t('commentHeadline')}</p>
          <p className="italic text-stone-800">{props.hotelRating.comment}</p>
        </div>
      )}
    </div>
  )
}
